/**
 * `ModuleSerienbrief` — the Stammdaten → Kommunikation → Serienbriefe module
 * screen (1:1 with the prototype `showSerienbrief` block, source lines 965–1045
 * + the floating "Versandkanäle" overlay at line 1844). A letter-builder: step
 * dots + Bearbeiten/Vorschau toggle, a formatting toolbar, and the
 * "Mietbescheinigung 2024" letter preview sheet with green placeholder chips.
 *
 * Sidebar = the shared `StammdatenSidebar` (active = "serienbrief"). The whole
 * interior is `position:relative` so the Versandkanäle callout can float at the
 * bottom-right, exactly as in the prototype.
 */
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Braces,
  ChevronDown,
  ChevronRight,
  Italic,
  KeyRound,
  List,
  ListOrdered,
  Mail,
  Paperclip,
  Pencil,
  Send,
  Stamp,
  Underline,
} from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { useTranslations } from "next-intl";
import { FloatingCallout, StatusDot } from "../../hero-primitives-app";
import { StammdatenSidebar } from "../../hero-stammdaten-sidebar";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";

/** Inline-block green placeholder chip ({Anrede}, {Vorname}, …). */
function Chip(props: { readonly children: ReactNode }): ReactNode {
  return (
    <span
      style={{
        display: "inline-block",
        background: "#E7F3EC",
        color: "#2E8557",
        fontWeight: 500,
        padding: "0 6px",
        borderRadius: 5,
      }}
    >
      {props.children}
    </span>
  );
}

/** One step dot in the 4-step wizard rail (active = filled, else outlined). */
function StepDot(props: {
  readonly n: number;
  readonly active?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <span
      style={{
        width: 26,
        height: 26,
        borderRadius: "50%",
        background: active ? H.accent : "transparent",
        border: active ? "none" : "1.5px solid #DDE0E5",
        color: active ? "#fff" : "rgba(11,15,25,0.42)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      {props.n}
    </span>
  );
}

const STEP_CONNECTOR: CSSProperties = {
  width: 30,
  height: 1.5,
  background: "#E2E5EA",
  display: "inline-block",
};

/** A square icon button in the formatting toolbar. */
function ToolbarIcon(props: {
  readonly children: ReactNode;
  readonly active?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <span
      style={{
        width: 30,
        height: 30,
        borderRadius: 7,
        background: active ? "#F1F2F4" : "transparent",
        color: active ? H.ink : "rgba(11,15,25,0.7)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      {props.children}
    </span>
  );
}

const TOOLBAR_DROPDOWN: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 7,
  height: 30,
  padding: "0 10px",
  border: "1px solid #E7E9ED",
  borderRadius: 7,
  background: "#fff",
  fontSize: 12.5,
  cursor: "pointer",
};

const TOOLBAR_SEP: CSSProperties = {
  width: 1,
  height: 20,
  background: "#E7E9ED",
  margin: "0 3px",
  display: "inline-block",
};

/** One row in the floating "Versandkanäle" callout. */
function ChannelRow(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  const t = useTranslations("marketing.moduleSerienbrief");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: "5px 0",
      }}
    >
      <span
        style={{
          width: 26,
          height: 26,
          flexShrink: 0,
          borderRadius: 7,
          background: H.accentSoft,
          color: H.accent,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {props.icon}
      </span>
      <span style={{ flex: 1, fontSize: 12.5, fontWeight: 600, color: H.ink }}>
        {props.label}
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10.5,
          fontWeight: 600,
          color: H.green,
        }}
      >
        <StatusDot color={H.green} size={6} />
        {t("verbunden")}
      </span>
    </div>
  );
}

/**
 * The floating "Versandkanäle" callout (frame-level overlay), prototype line
 * 1844. Positions itself absolutely relative to the aurora frame so it
 * overflows the window (rendered via the registry `Overlay` slot).
 */
export function SerienbriefChannelsOverlay(): ReactNode {
  const t = useTranslations("marketing.moduleSerienbrief");
  return (
    <FloatingCallout
      tabLabel={
        <>
          <Send width={10} height={10} strokeWidth={2} />
          {t("versandkanaele")}
        </>
      }
      style={{
        position: "absolute",
        right: 20,
        bottom: 18,
        zIndex: 35,
        boxShadow:
          "0 36px 72px -22px rgba(11,15,25,0.46), 0 12px 26px -12px rgba(11,15,25,0.26)",
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          color: "rgba(11,15,25,0.5)",
          marginBottom: 10,
          lineHeight: 1.35,
        }}
      >
        {t("alleKanaeleVerbunden")}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <ChannelRow
          icon={<Stamp width={14} height={14} strokeWidth={1.9} />}
          label={t("ePost")}
        />
        <ChannelRow
          icon={<Mail width={14} height={14} strokeWidth={1.9} />}
          label={t("eMail")}
        />
        <ChannelRow
          icon={<KeyRound width={14} height={14} strokeWidth={1.9} />}
          label={t("portal")}
        />
      </div>
    </FloatingCallout>
  );
}

export function ModuleSerienbrief(props: ModuleProps): ReactNode {
  const t = useTranslations("marketing.moduleSerienbrief");
  return (
    <div style={{ display: "flex", height: MODULE_HEIGHT }}>
      <StammdatenSidebar active="serienbrief" setActive={props.setActive} />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: "20px 22px",
          overflow: "hidden",
        }}
      >
        {/* Header: title + step rail + Bearbeiten/Vorschau toggle + Weiter */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            marginBottom: 18,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span
                style={{ fontSize: 18, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {t("mietbescheinigung2024")}
              </span>
              <span
                style={{
                  color: "rgba(11,15,25,0.34)",
                  display: "inline-flex",
                  cursor: "pointer",
                }}
              >
                <Pencil width={14} height={14} strokeWidth={1.8} />
              </span>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <StepDot n={1} active />
              <span style={STEP_CONNECTOR} />
              <StepDot n={2} />
              <span style={STEP_CONNECTOR} />
              <StepDot n={3} />
              <span style={STEP_CONNECTOR} />
              <StepDot n={4} />
            </div>
          </div>

          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: 3,
              background: "#F1F2F4",
              borderRadius: 9,
              gap: 2,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                padding: "5px 13px",
                borderRadius: 7,
                background: "#fff",
                fontSize: 12.5,
                fontWeight: 600,
                color: H.ink,
                boxShadow: "0 1px 2px rgba(11,15,25,0.08)",
              }}
            >
              {t("bearbeiten")}
            </span>
            <span
              style={{
                padding: "5px 13px",
                borderRadius: 7,
                fontSize: 12.5,
                color: "rgba(11,15,25,0.55)",
                cursor: "pointer",
              }}
            >
              {t("vorschau")}
            </span>
          </div>

          <button
            type="button"
            style={{
              appearance: "none",
              cursor: "pointer",
              fontFamily: H.sans,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              height: 36,
              padding: "0 15px",
              border: "none",
              borderRadius: 9,
              background: H.accent,
              color: "#fff",
              fontSize: 13,
              fontWeight: 600,
              flexShrink: 0,
            }}
          >
            {t("weiter")}{" "}
            <ChevronRight width={15} height={15} strokeWidth={2.2} />
          </button>
        </div>

        {/* Formatting toolbar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            flexWrap: "wrap",
            padding: "8px 10px",
            border: "1px solid #ECEEF1",
            borderBottom: "none",
            borderRadius: "12px 12px 0 0",
            background: "#FCFCFB",
          }}
        >
          <span style={TOOLBAR_DROPDOWN}>
            {t("text")} <ChevronDown width={13} height={13} strokeWidth={1.9} />
          </span>
          <span style={TOOLBAR_DROPDOWN}>
            13 <ChevronDown width={13} height={13} strokeWidth={1.9} />
          </span>
          <span style={TOOLBAR_SEP} />
          <ToolbarIcon>
            <Bold width={15} height={15} strokeWidth={2.2} />
          </ToolbarIcon>
          <ToolbarIcon>
            <Italic width={15} height={15} strokeWidth={2} />
          </ToolbarIcon>
          <ToolbarIcon>
            <Underline width={15} height={15} strokeWidth={2} />
          </ToolbarIcon>
          <span
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              display: "inline-flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(11,15,25,0.7)",
              cursor: "pointer",
              gap: 1,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: 1 }}>
              {t("a")}
            </span>
            <span
              style={{
                width: 15,
                height: 2.5,
                borderRadius: 2,
                background: "#D8453E",
              }}
            />
          </span>
          <span style={TOOLBAR_SEP} />
          <ToolbarIcon>
            <List width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <ToolbarIcon>
            <ListOrdered width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <span style={TOOLBAR_SEP} />
          <ToolbarIcon active>
            <AlignLeft width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <ToolbarIcon>
            <AlignCenter width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <ToolbarIcon>
            <AlignRight width={15} height={15} strokeWidth={1.9} />
          </ToolbarIcon>
          <span style={{ marginLeft: "auto" }} />
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 30,
              padding: "0 11px",
              border: "1px solid #E7E9ED",
              borderRadius: 7,
              background: "#fff",
              fontSize: 12.5,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <span style={{ color: H.accent, display: "inline-flex" }}>
              <Braces width={14} height={14} strokeWidth={1.9} />
            </span>
            {t("variablen")}{" "}
            <ChevronDown width={13} height={13} strokeWidth={1.9} />
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              height: 30,
              padding: "0 11px",
              border: "1px solid #E7E9ED",
              borderRadius: 7,
              background: "#fff",
              fontSize: 12.5,
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            <Paperclip width={14} height={14} strokeWidth={1.9} />
            {t("anhaenge")}
          </span>
        </div>

        {/* Letter preview sheet */}
        <div
          style={{
            border: "1px solid #ECEEF1",
            borderRadius: "0 0 12px 12px",
            background: "#F4F5F7",
            padding: "26px 0 38px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 660,
              maxWidth: "100%",
              background: "#fff",
              border: "1px solid #E6E8EC",
              borderRadius: 3,
              boxShadow: "0 12px 32px -14px rgba(11,15,25,0.2)",
              padding: "52px 60px 60px",
              fontSize: 13.5,
              lineHeight: 1.72,
              color: "#1C2230",
            }}
          >
            {/* Letterhead */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                paddingBottom: 13,
                borderBottom: `2px solid ${H.accent}`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 9,
                    background: H.ink,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width={15}
                    height={12}
                    style={{ display: "block" }}
                    aria-hidden="true"
                  >
                    <path
                      d="M7.5 1.6 L13.4 10.4 L1.6 10.4 Z"
                      style={{
                        fill: "#fff",
                        stroke: "#fff",
                        strokeWidth: 2.6,
                        strokeLinejoin: "round",
                      }}
                    />
                  </svg>
                </span>
                <div>
                  <div
                    style={{
                      fontSize: 15.5,
                      fontWeight: 700,
                      letterSpacing: "0.01em",
                      color: H.ink,
                      lineHeight: 1.1,
                    }}
                  >
                    {t("powerprozesse")}
                  </div>
                  <div
                    style={{
                      fontSize: 9.5,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: H.accent,
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    {t("hausverwaltungGmbh")}
                  </div>
                </div>
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontSize: 10,
                  lineHeight: 1.7,
                  color: "rgba(11,15,25,0.5)",
                }}
              >
                {t.rich("absenderAnschrift", { br: () => <br /> })}
              </div>
            </div>

            {/* Sender line + recipient block */}
            <div style={{ marginTop: 34 }}>
              <div
                style={{
                  fontSize: 9,
                  color: "rgba(11,15,25,0.5)",
                  borderBottom: "0.5px solid rgba(11,15,25,0.32)",
                  paddingBottom: 2,
                  display: "inline-block",
                  letterSpacing: "0.01em",
                }}
              >
                {t("powerprozesseGmbhAdenauerring1")}
              </div>
              <div style={{ marginTop: 15, lineHeight: 1.62 }}>
                <div>
                  <Chip>{t("anrede")}</Chip> <Chip>{t("vorname")}</Chip>{" "}
                  <Chip>{t("nachname")}</Chip>
                </div>
                <div style={{ marginTop: 2 }}>
                  <Chip>{t("strasseNr")}</Chip>
                </div>
                <div style={{ marginTop: 2 }}>
                  <Chip>{t("plz")}</Chip> <Chip>{t("ort")}</Chip>
                </div>
              </div>
            </div>

            {/* Date */}
            <div
              style={{
                textAlign: "right",
                marginTop: 24,
                color: "rgba(11,15,25,0.7)",
              }}
            >
              {t("worms")} <Chip>{t("datum")}</Chip>
            </div>

            {/* Subject */}
            <div style={{ marginTop: 26, fontWeight: 700, color: H.ink }}>
              {t("mietbescheinigungFuerMietverhaeltnis")}{" "}
              <Chip>{t("mietobjekt")}</Chip>
            </div>

            {/* Body */}
            <div style={{ marginTop: 20 }}>
              {t("sehrGeehrteR")} <Chip>{t("nachname")}</Chip>,
            </div>
            <div style={{ marginTop: 14 }}>
              {t("hiermitBestaetigen")} <Chip>{t("anrede")}</Chip>{" "}
              <Chip>{t("vorname")}</Chip> <Chip>{t("nachname")}</Chip>{" "}
              {t("seit")} <Chip>{t("mietbeginn")}</Chip> {t("mieterWohnung")}{" "}
              <Chip>{t("mietobjekt")}</Chip> {t("in")}{" "}
              <Chip>{t("strasseNr")}</Chip>, <Chip>{t("plz")}</Chip>{" "}
              <Chip>{t("ort")}</Chip> {t("ist")}
            </div>
            <div style={{ marginTop: 13 }}>
              {t("monatlicheGesamtmieteBetraegtDerzeit")}{" "}
              <Chip>{t("gesamtmiete")}</Chip> {t("nettokaltmiete")}
              <Chip>{t("kaltmiete")}</Chip> {t("zzgl")}{" "}
              <Chip>{t("nebenkosten")}</Chip>{" "}
              {t("vorauszahlungFuerBetriebsHeizkosten")}
            </div>
            <div style={{ marginTop: 13 }}>
              {t("dieseBescheinigungWunschMietpartei")}{" "}
              <Chip>{t("behoerde")}</Chip> {t("ausgestellt")}
            </div>
            <div style={{ marginTop: 13 }}>
              {t("fuerRueckfragenStehenIhnen")}
            </div>
            <div style={{ marginTop: 22 }}>{t("freundlichenGruessen")}</div>
            <div style={{ marginTop: 30 }}>
              <Chip>{t("sachbearbeiter")}</Chip>
              <br />
              <span style={{ color: "rgba(11,15,25,0.55)" }}>
                {t("powerprozesseHausverwaltungGmbh")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
